import type { ContextMessage } from '../types/chat'

import { errorMessageFrom } from '@moeru/std'
import { ContextUpdateStrategy } from '@proj-airi/server-sdk'

import { frameEvidence } from './velis-prompt'

/** Browser and screen slots stay out of the prompt after this age. */
export const perceptionTtlMs = 3 * 60 * 1000

const perceptionTextLimit = 1200

export const perceptionContextKeys = ['velis:browser', 'velis:screen'] as const

export type PerceptionPermission = 'off' | 'on-demand'

export interface PerceptionPermissions {
  browser: PerceptionPermission
  screen: PerceptionPermission
}

export type BrowserLane = 'web:page' | 'web:video' | 'web:subtitle'

export interface BrowserUpdate {
  lane: BrowserLane
  source: string
  sourceRef?: string
  capturedAt: number
  title?: string
  url?: string
  text: string
}

export interface BrowserSlot {
  kind: 'browser'
  source: string
  sourceRef?: string
  capturedAt: number
  title?: string
  url?: string
  summary?: string
  video?: string
  subtitle?: string
}

export interface ScreenSlot {
  kind: 'screen'
  source: string
  sourceRef?: string
  capturedAt: number
  summary: string
}

export interface PerceptionTrace {
  kind: 'browser' | 'screen'
  source: string
  capturedAt?: number
  injected: boolean
  reason: 'injected' | 'permission-off' | 'expired' | 'unavailable' | 'failed'
  approximateTokens: number
  error?: string
}

export interface ScreenRead {
  text: string
  sourceRef: string
  capturedAt: number
}

export type ScreenReadResult = { ok: true, read: ScreenRead } | { ok: false, error: string }

export interface PerceptionAssembly {
  messages: ContextMessage[]
  traces: PerceptionTrace[]
  armedKeys: string[]
  browser?: BrowserSlot
  screen?: ScreenSlot
}

const browserLanes = new Set<BrowserLane>(['web:page', 'web:video', 'web:subtitle'])

function stringField(metadata: unknown, key: string) {
  if (!metadata || typeof metadata !== 'object')
    return undefined
  const value = (metadata as Record<string, unknown>)[key]
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function isBrowserLane(lane: string | undefined): lane is BrowserLane {
  return lane === 'web:page' || lane === 'web:video' || lane === 'web:subtitle'
}

/**
 * Reads one browser `context:update` into the single browser slot.
 * Page, video, and subtitle lanes share that slot.
 */
export function browserUpdateFromContext(input: {
  lane?: string
  text?: string
  metadata?: unknown
  capturedAt: number
}): BrowserUpdate | undefined {
  if (!isBrowserLane(input.lane))
    return undefined
  return {
    lane: input.lane,
    source: 'web-extension',
    sourceRef: stringField(input.metadata, 'url'),
    capturedAt: input.capturedAt,
    title: stringField(input.metadata, 'title'),
    url: stringField(input.metadata, 'url'),
    text: input.text?.trim() ?? '',
  }
}

function samePage(current: BrowserSlot | undefined, update: BrowserUpdate) {
  return Boolean(current?.url && update.url && current.url === update.url)
}

/**
 * Stores the latest browser update in one slot.
 * A closed permission drops the slot, including updates that arrive while it is closed.
 */
export function replaceBrowserSlot(
  current: BrowserSlot | undefined,
  update: BrowserUpdate,
  permission: PerceptionPermission,
): BrowserSlot | undefined {
  if (permission === 'off')
    return undefined
  if (!browserLanes.has(update.lane))
    return current

  const keep = !update.url || samePage(current, update) ? current : undefined
  const slot: BrowserSlot = {
    kind: 'browser',
    source: update.source,
    sourceRef: update.url ?? update.sourceRef ?? keep?.sourceRef,
    capturedAt: update.capturedAt,
    title: update.title ?? keep?.title,
    url: update.url ?? keep?.url,
    summary: keep?.summary,
    video: keep?.video,
    subtitle: keep?.subtitle,
  }
  if (update.lane === 'web:page') {
    slot.summary = update.text
    if (!keep) {
      slot.video = undefined
      slot.subtitle = undefined
    }
  }
  if (update.lane === 'web:video')
    slot.video = update.text
  if (update.lane === 'web:subtitle')
    slot.subtitle = update.text
  return slot
}

export function requestsBrowserContext(message: string) {
  return /当前网页|这个网页|当前页面|这个页面|看看网页|看一下网页|看下网页|看看页面|看一下页面/.test(message)
    || /\b(?:this|current|the) (?:web ?page|page|tab)\b/i.test(message)
    || /\blook at (?:this |the )?(?:page|webpage|website|tab)\b/i.test(message)
}

export function requestsScreenContext(message: string) {
  const trimmed = message.trim()
  if (/^(?:看一下这个|看看这个|看一下|look at this)[.!！。]?$/i.test(trimmed))
    return true
  return /当前屏幕|这个屏幕|屏幕上|看看屏幕|看一下屏幕|看下屏幕|看屏幕/.test(message)
    || /\b(?:this|current|the|my) screen\b/i.test(message)
    || /\blook at (?:this |the |my )?screen\b/i.test(message)
}

export function approximateTokenCount(text: string) {
  if (!text)
    return 0
  return Math.ceil(text.length / 4)
}

function fresh(capturedAt: number, now: number) {
  return capturedAt <= now && now - capturedAt <= perceptionTtlMs
}

function limitText(text: string) {
  return text.trim().slice(0, perceptionTextLimit)
}

function renderBrowser(slot: BrowserSlot) {
  const lines = [
    slot.title ? `Title: ${slot.title}` : undefined,
    slot.url ? `URL: ${slot.url}` : undefined,
    slot.summary,
    slot.video,
    slot.subtitle,
  ].filter((line): line is string => Boolean(line?.trim()))
  return limitText(lines.join('\n'))
}

export interface HeldContextEntry {
  slot: 'velis:browser' | 'velis:screen'
  summary: string
  capturedAt: number
}

/**
 * Reads the browser and screen slots that are still fresh.
 * A disabled or expired slot stays out. This read does not capture a new frame.
 *
 * @example
 * readHeldContext({
 *   now: 2_000,
 *   permissions: { browser: 'on-demand', screen: 'off' },
 *   browser: { kind: 'browser', source: 'web', capturedAt: 1_000, summary: 'Velis plan' },
 * })
 * // => [{ slot: 'velis:browser', summary: 'Velis plan', capturedAt: 1_000 }]
 */
export function readHeldContext(input: {
  now: number
  permissions: PerceptionPermissions
  browser?: BrowserSlot
  screen?: ScreenSlot
}): HeldContextEntry[] {
  const entries: HeldContextEntry[] = []
  if (input.permissions.browser !== 'off' && input.browser && fresh(input.browser.capturedAt, input.now)) {
    const summary = renderBrowser(input.browser)
    if (summary)
      entries.push({ slot: 'velis:browser', summary, capturedAt: input.browser.capturedAt })
  }
  if (input.permissions.screen !== 'off' && input.screen && fresh(input.screen.capturedAt, input.now)) {
    const summary = limitText(input.screen.summary)
    if (summary)
      entries.push({ slot: 'velis:screen', summary, capturedAt: input.screen.capturedAt })
  }
  return entries
}

function perceptionMessage(kind: 'browser' | 'screen', text: string, capturedAt: number, source: string): ContextMessage {
  const contextId = kind === 'browser' ? 'velis:browser' : 'velis:screen'
  return {
    id: `${contextId}:context`,
    contextId,
    strategy: ContextUpdateStrategy.ReplaceSelf,
    text: frameEvidence(kind, text),
    createdAt: capturedAt,
    metadata: {
      source: {
        id: contextId,
        labels: { origin: source },
      },
    },
  }
}

function withheld(kind: 'browser' | 'screen', source: string, reason: PerceptionTrace['reason'], error?: string, capturedAt?: number): PerceptionTrace {
  return {
    kind,
    source,
    capturedAt,
    injected: false,
    reason,
    approximateTokens: 0,
    ...(error ? { error } : {}),
  }
}

/**
 * Captures one frame, asks the vision model once, and returns text only.
 * The image data URL stays inside this call.
 */
export async function readScreenSummary(input: {
  capture: () => Promise<{ imageDataUrl: string, sourceRef: string } | { error: string }>
  interpret: (imageDataUrl: string) => Promise<string>
}): Promise<ScreenReadResult> {
  let captured: { imageDataUrl: string, sourceRef: string } | { error: string }
  try {
    captured = await input.capture()
  }
  catch (error) {
    return { ok: false, error: errorMessageFrom(error) ?? 'Screen capture failed.' }
  }
  if ('error' in captured)
    return { ok: false, error: captured.error }

  try {
    const text = (await input.interpret(captured.imageDataUrl)).trim()
    if (!text)
      return { ok: false, error: 'Screen summary is empty.' }
    return {
      ok: true,
      read: {
        text,
        sourceRef: captured.sourceRef,
        capturedAt: Date.now(),
      },
    }
  }
  catch (error) {
    return { ok: false, error: errorMessageFrom(error) ?? 'Screen summary failed.' }
  }
}

/**
 * Selects the browser and screen blocks for one user message.
 * A failed screen read leaves the browser block and the rest of the turn intact.
 */
export async function collectPerceptionForTurn(input: {
  message: string
  now: number
  permissions: PerceptionPermissions
  browser?: BrowserSlot
  screen?: ScreenSlot
  readScreen?: () => Promise<ScreenReadResult>
}): Promise<PerceptionAssembly> {
  const traces: PerceptionTrace[] = []
  const messages: ContextMessage[] = []
  const armedKeys: string[] = []
  let browser = input.browser
  let screen = input.screen

  if (requestsBrowserContext(input.message)) {
    if (input.permissions.browser === 'off') {
      browser = undefined
      traces.push(withheld('browser', browserSource(input.browser), 'permission-off', undefined, input.browser?.capturedAt))
    }
    else if (!browser) {
      traces.push(withheld('browser', 'web-extension', 'unavailable'))
    }
    else if (!fresh(browser.capturedAt, input.now)) {
      traces.push(withheld('browser', browser.source, 'expired', undefined, browser.capturedAt))
    }
    else {
      const text = renderBrowser(browser)
      if (!text) {
        traces.push(withheld('browser', browser.source, 'unavailable', undefined, browser.capturedAt))
      }
      else {
        const message = perceptionMessage('browser', text, browser.capturedAt, browser.source)
        messages.push(message)
        armedKeys.push('velis:browser')
        traces.push({
          kind: 'browser',
          source: browser.source,
          capturedAt: browser.capturedAt,
          injected: true,
          reason: 'injected',
          approximateTokens: approximateTokenCount(message.text),
        })
      }
    }
  }

  if (requestsScreenContext(input.message)) {
    if (input.permissions.screen === 'off') {
      screen = undefined
      traces.push(withheld('screen', screenSource(input.screen), 'permission-off', undefined, input.screen?.capturedAt))
    }
    else if (!input.readScreen) {
      screen = undefined
      traces.push(withheld('screen', 'screen-capture', 'unavailable'))
    }
    else {
      try {
        const result = await input.readScreen()
        if (!result.ok) {
          screen = undefined
          traces.push(withheld('screen', 'screen-capture', 'failed', result.error))
        }
        else {
          const text = limitText(result.read.text)
          screen = {
            kind: 'screen',
            source: 'screen-capture',
            sourceRef: result.read.sourceRef,
            capturedAt: input.now,
            summary: text,
          }
          const message = perceptionMessage('screen', text, screen.capturedAt, screen.source)
          messages.push(message)
          armedKeys.push('velis:screen')
          traces.push({
            kind: 'screen',
            source: screen.source,
            capturedAt: screen.capturedAt,
            injected: true,
            reason: 'injected',
            approximateTokens: approximateTokenCount(message.text),
          })
        }
      }
      catch (error) {
        screen = undefined
        traces.push(withheld('screen', 'screen-capture', 'failed', errorMessageFrom(error) ?? 'Screen summary failed.'))
      }
    }
  }

  return { messages, traces, armedKeys, browser, screen }
}

function browserSource(slot: BrowserSlot | undefined) {
  return slot?.source ?? 'web-extension'
}

function screenSource(slot: ScreenSlot | undefined) {
  return slot?.source ?? 'screen-capture'
}

/**
 * Removes browser and screen buckets that this turn did not select.
 * Other context buckets stay in the snapshot.
 */
export function omitUnarmedPerceptionContext<T>(snapshot: Record<string, T>, armedKeys: readonly string[]) {
  const armed = new Set(armedKeys)
  const perceptionKeys = new Set<string>(perceptionContextKeys)
  const visible: Record<string, T> = {}
  for (const [key, value] of Object.entries(snapshot)) {
    if (perceptionKeys.has(key) && !armed.has(key))
      continue
    visible[key] = value
  }
  return visible
}
